import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { PublicProfileService } from '../../services/PublicProfileService';
import { ReferralService } from '../../services/ReferralService';

export default function ReferralPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [code, setCode] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    PublicProfileService.getOrCreateInviteCode().then(setCode);
    ReferralService.getStats(user.id).then((stats) => setReferralCount(stats.referralCount));
  }, [isAuthenticated, user]);

  async function handleShare() {
    const link = ReferralService.getReferralLink(code);
    const shareText = t('referral.shareMessage', { link });
    if (navigator.share) {
      try { await navigator.share({ text: shareText, url: link }); return; } catch { /* cancelled or unsupported */ }
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable — link is still visible */ }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
        <div className="mx-auto max-w-lg space-y-5 text-center">
          <span className="text-5xl">🎁</span>
          <h1 className="text-2xl font-black text-slate-900">{t('referral.title')}</h1>
          <p className="text-sm text-slate-500">{t('referral.signInNotice')}</p>
          <button
            onClick={() => navigate('/login')}
            className="mx-auto mt-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3 text-sm font-bold text-white shadow-md"
          >
            {t('profile.loginArrow')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
      <div className="mx-auto max-w-lg space-y-5">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {t('profile.backToHome')}
        </button>

        <div className="rounded-3xl bg-gradient-to-r from-indigo-600 to-violet-600 p-6 text-white shadow-xl">
          <span className="text-4xl">🎁</span>
          <h1 className="mt-2 text-2xl font-black">{t('referral.title')}</h1>
          <p className="mt-1 text-sm text-white/80">{t('referral.reward')}</p>
        </div>

        <div className="rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm text-center">
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('friends.yourCode')}</p>
          <p className="mt-2 font-mono text-3xl font-black tracking-widest text-indigo-700">{code || '······'}</p>
          <button
            onClick={handleShare}
            className="mt-4 w-full rounded-xl bg-indigo-600 py-3 text-sm font-bold text-white shadow-md transition hover:bg-indigo-700"
          >
            {copied ? t('referral.linkCopied') : t('referral.shareLink')}
          </button>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm text-center">
          <p className="text-3xl font-black text-slate-900">{referralCount}</p>
          <p className="mt-1 text-sm font-semibold text-slate-500">{t('referral.friendsInvited')}</p>
        </div>
      </div>
    </div>
  );
}
